import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Skeleton from "@mui/material/Skeleton";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Eye,
  EyeOff,
  KeyRound,
  Search,
  ShieldCheck,
  UserPlus,
} from "lucide-react";
import { toast } from "react-toastify";
import { useLazySearchRegisteredUserByEmailQuery } from "../../../../features/user/userApiSlice";
import {
  useSaveShopMemberAccessMutation,
  useLazyCheckShopMemberUsernameAvailabilityQuery,
  useLazyCheckShopMemberPasswordAvailabilityQuery,
  useSetShopMemberCredentialsMutation,
} from "../../../../features/shop/shopMemberApiSlice";
import { decodeId } from "../../../../utils/idObfuscator";
import RegisteredUserAvatar from "../RegisteredUserAvatar";
import "../ModalEmail/style.css";

const accessAreas = [
  { id: "business-dashboard", label: "Business dashboard" },
  { id: "messanger", label: "Messenger" },
  { id: "shop-reports", label: "Shop reports and suggestions" },
  { id: "post-edit-items", label: "Post and edit items" },
];

const USERNAME_PATTERN = /^[a-z][a-z0-9._-]{2,19}$/i;
const PASSWORD_PATTERN =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9\s])\S{12,128}$/;

const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const ModalEmailone = ({
  handleClosesecond,
  secondmodal,
  shopId,
}) => {
  const resolvedShopId = decodeId(shopId) || shopId || "";

  // Step state: 1 = Search User, 2 = Permissions, 3 = Credentials
  const [step, setStep] = useState(1);

  // Step 1: Search state
  const [email, setEmail] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [
    searchRegisteredUser,
    { currentData, originalArgs, isFetching, isError, error, reset },
  ] = useLazySearchRegisteredUserByEmailQuery();

  // Step 2: Access areas state
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [membershipId, setMembershipId] = useState("");
  const [isSavingAccess, setIsSavingAccess] = useState(false);
  const [accessError, setAccessError] = useState("");
  const [saveShopMemberAccess] = useSaveShopMemberAccessMutation();

  // Step 3: Credentials state
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [checkUsername, usernameAvailability] =
    useLazyCheckShopMemberUsernameAvailabilityQuery();
  const [checkPassword, passwordAvailability] =
    useLazyCheckShopMemberPasswordAvailabilityQuery();
  const [setCredentials, { isLoading: isSavingCredentials }] =
    useSetShopMemberCredentialsMutation();

  const cleanMembershipId = decodeId(membershipId) || membershipId || "";
  const cleanShopId = resolvedShopId;

  // Username validation
  const normalizedUsername = username.trim().toLowerCase();
  const usernameFormatIsValid = USERNAME_PATTERN.test(normalizedUsername);
  const matchingAvailability =
    usernameAvailability.originalArgs?.username === normalizedUsername &&
    usernameAvailability.originalArgs?.membershipId === cleanMembershipId;
  const usernameAvailable = matchingAvailability
    ? usernameAvailability.currentData?.data?.available
    : undefined;
  const usernameError = !normalizedUsername
    ? "Username is required."
    : !usernameFormatIsValid
      ? "Use 3–20 characters, start with a letter, and use letters, numbers, dots, underscores, or hyphens."
      : usernameAvailable === false
        ? "This username is already taken."
        : usernameAvailability.isError && matchingAvailability
          ? usernameAvailability.error?.data?.message ||
            "Unable to check username availability."
          : "";

  // Password validation
  const passwordFormatIsValid = PASSWORD_PATTERN.test(password);
  const matchingPasswordAvailability =
    passwordAvailability.originalArgs?.password === password &&
    passwordAvailability.originalArgs?.membershipId === cleanMembershipId;
  const passwordAvailable = matchingPasswordAvailability
    ? passwordAvailability.currentData?.data?.available
    : undefined;

  const passwordError = !password
    ? "Password is required."
    : !passwordFormatIsValid
      ? "Use 12–128 characters with uppercase and lowercase letters, a number, and a symbol."
      : passwordAvailable === false
        ? "This password is already in use by another shop member."
        : passwordAvailability.isError && matchingPasswordAvailability
          ? passwordAvailability.error?.data?.message ||
            "Unable to verify password uniqueness."
          : "";

  const formIsValid =
    usernameFormatIsValid &&
    usernameAvailable === true &&
    passwordFormatIsValid &&
    passwordAvailable === true &&
    Boolean(cleanMembershipId);

  // Search results logic
  const hasSearchTerm = Boolean(email.trim());
  const canInviteEmail = isValidEmail(email);
  const searchedEmail =
    typeof originalArgs === "string"
      ? originalArgs
      : originalArgs?.email || "";
  const searchMatchesInput =
    searchedEmail.toLowerCase() === email.trim().toLowerCase();
  const foundUser = searchMatchesInput ? currentData?.data?.user : null;
  const matchingUser =
    foundUser?.email?.toLowerCase() === email.trim().toLowerCase()
      ? foundUser
      : null;
  const isAwaitingAcceptance =
    matchingUser?.invitationStatus === "awaiting_acceptance";
  const isActiveMember = matchingUser?.invitationStatus === "active";
  const isOwner = Boolean(matchingUser?.isOwner);
  const cannotInvite = isAwaitingAcceptance || isOwner;
  const noUserFound =
    searchMatchesInput &&
    isError &&
    (error?.status === 404 || error?.data?.message === "No user found");

  // Reset all state when modal closes
  useEffect(() => {
    if (!secondmodal) {
      setStep(1);
      setEmail("");
      setSelectedUser(null);
      setIsDebouncing(false);
      setSelectedAreas([]);
      setMembershipId("");
      setIsSavingAccess(false);
      setAccessError("");
      setUsername("");
      setPassword("");
      setShowPassword(false);
      setSubmitted(false);
      setSubmitError("");
      if (typeof reset === "function") reset();
    }
  }, [secondmodal, reset]);

  // Debounce search query
  useEffect(() => {
    if (!secondmodal || step !== 1 || !isValidEmail(email)) {
      setIsDebouncing(false);
      return undefined;
    }

    setIsDebouncing(true);
    const timeoutId = window.setTimeout(() => {
      setIsDebouncing(false);
      searchRegisteredUser(
        {
          email: email.trim(),
          shopId: resolvedShopId,
        },
        false,
      );
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [email, resolvedShopId, searchRegisteredUser, secondmodal, step]);

  // Step 3 live username check
  useEffect(() => {
    if (
      !secondmodal ||
      step !== 3 ||
      !cleanMembershipId ||
      !usernameFormatIsValid
    ) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      checkUsername({
        shopId: cleanShopId,
        username: normalizedUsername,
        membershipId: cleanMembershipId,
      });
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [
    checkUsername,
    cleanMembershipId,
    cleanShopId,
    normalizedUsername,
    secondmodal,
    step,
    usernameFormatIsValid,
  ]);

  // Step 3 live password check
  useEffect(() => {
    if (
      !secondmodal ||
      step !== 3 ||
      !cleanMembershipId ||
      !passwordFormatIsValid
    ) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      checkPassword({
        shopId: cleanShopId,
        password,
        membershipId: cleanMembershipId,
      });
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [
    checkPassword,
    cleanMembershipId,
    cleanShopId,
    password,
    passwordFormatIsValid,
    secondmodal,
    step,
  ]);

  const handleClose = () => {
    handleClosesecond();
  };

  const handleSelectUser = (user) => {
    if (user?.isOwner || user?.invitationStatus === "awaiting_acceptance") {
      return;
    }
    setSelectedUser(user);
    setEmail("");
    setIsDebouncing(false);
  };

  // Step 1 -> Step 2
  const handleStep1Continue = (event) => {
    if (event) event.preventDefault();

    const userToUse =
      selectedUser || (matchingUser && !cannotInvite ? matchingUser : null);

    if (
      !userToUse ||
      userToUse?.isOwner ||
      userToUse?.invitationStatus === "awaiting_acceptance"
    ) {
      return;
    }

    if (!selectedUser) {
      setSelectedUser(userToUse);
    }
    setAccessError("");
    setStep(2);
  };

  // Step 2 -> Step 3
  const handleStep2Continue = async (event) => {
    if (event) event.preventDefault();
    setAccessError("");

    if (selectedAreas.length === 0) {
      setAccessError("Select at least one access area to continue.");
      return;
    }

    if (!resolvedShopId || !selectedUser?._id) {
      setAccessError("A shop and registered user are required.");
      return;
    }

    setIsSavingAccess(true);
    try {
      const response = await saveShopMemberAccess({
        shopId: resolvedShopId,
        userId: selectedUser._id,
        role: selectedUser.role || "member",
        accessAreas: selectedAreas,
      }).unwrap();

      if (response?.data?.alreadyActive) {
        toast.success(
          response?.message || "User already has active access to this shop",
          { position: "top-center" },
        );
        handleClose();
        return;
      }

      const newMembershipId = response?.data?.membershipId || "";
      setMembershipId(newMembershipId);
      setSubmitted(false);
      setSubmitError("");
      setStep(3);
    } catch (err) {
      setAccessError(
        err?.data?.message || "Unable to save access areas. Please try again.",
      );
    } finally {
      setIsSavingAccess(false);
    }
  };

  // Step 3 Submit
  const handleStep3Submit = async (event) => {
    if (event) event.preventDefault();
    setSubmitted(true);
    setSubmitError("");

    if (!formIsValid) {
      if (!usernameFormatIsValid) {
        setSubmitError(usernameError || "Please enter a valid username.");
        return;
      }
      if (usernameAvailable !== true) {
        setSubmitError(
          usernameAvailable === false
            ? "This username is already taken."
            : usernameError || "Checking username availability...",
        );
        return;
      }
      if (!passwordFormatIsValid) {
        setSubmitError(passwordError || "Please enter a strong password.");
        return;
      }
      if (passwordAvailable !== true) {
        setSubmitError(
          passwordAvailable === false
            ? "This password is already in use by another shop member."
            : passwordError || "Checking password uniqueness...",
        );
        return;
      }
      if (!cleanMembershipId) {
        setSubmitError("Membership record missing. Please try inviting again.");
        return;
      }
      return;
    }

    try {
      const result = await setCredentials({
        shopId: cleanShopId,
        membershipId: cleanMembershipId,
        username: normalizedUsername,
        password,
      }).unwrap();

      const sharePath = result?.data?.membership?.sharePath;
      if (sharePath) {
        const fullShareUrl = `${window.location.origin}${sharePath}`;
        navigator.clipboard?.writeText?.(fullShareUrl).catch(() => {});
        toast.success(
          "Shop access invitation created! Protected share link copied to clipboard.",
          { position: "top-center" },
        );
      } else {
        toast.success("Shop access granted!", { position: "top-center" });
      }

      if (!result.notificationSent) {
        toast.warn(
          "The invitation was saved, but the member notification could not be sent.",
          { position: "top-center" },
        );
      }

      handleClose();
    } catch (err) {
      setSubmitError(
        err?.data?.message || "Unable to save shop access. Please try again.",
      );
    }
  };

  return (
    <Modal
      className="shop-invite-modal"
      centered
      show={secondmodal}
      onHide={handleClose}
      aria-labelledby="shop-member-invite-title"
    >
      <Modal.Header closeButton className="shop-invite-header">
        <div className="shop-invite-heading">
          <span className="shop-invite-heading-icon">
            {step === 1 && <UserPlus size={19} />}
            {step === 2 && <ShieldCheck size={19} />}
            {step === 3 && <KeyRound size={19} />}
          </span>
          <div>
            <span className="shop-invite-step-badge">
              Step {step} of 3
            </span>
            <Modal.Title id="shop-member-invite-title">
              {step === 1 && "Add a shop admin"}
              {step === 2 && "Choose member access"}
              {step === 3 && "Set up shop access"}
            </Modal.Title>
            <p>
              {step === 1 && "Choose someone to invite to your shop."}
              {step === 2 && "Select the areas this member can access."}
              {step === 3 && "Create the login details for this member."}
            </p>
          </div>
        </div>
      </Modal.Header>

      {/* STEP 1: Search User */}
      {step === 1 && (
        <Modal.Body className="shop-invite-body">
          <Form noValidate onSubmit={handleStep1Continue}>
            <Form.Label htmlFor="shop-member-email">Email address</Form.Label>
            <div className="shop-invite-search">
              <Search className="shop-invite-search-icon" size={17} />
              <Form.Control
                id="shop-member-email"
                type="email"
                value={email}
                autoComplete="email"
                placeholder="name@example.com"
                aria-label="Email address"
                aria-describedby="shop-member-invite-results"
                aria-invalid={!canInviteEmail && Boolean(email)}
                onChange={(event) => {
                  const value = event.target.value;
                  setEmail(value);
                  setSelectedUser(null);
                  setIsDebouncing(isValidEmail(value));
                  if (!value.trim() && typeof reset === "function") {
                    reset();
                  }
                }}
              />
              {email && (
                <button
                  className="shop-invite-clear"
                  type="button"
                  aria-label="Clear email"
                  onClick={() => {
                    setEmail("");
                    setSelectedUser(null);
                    setIsDebouncing(false);
                    if (typeof reset === "function") reset();
                  }}
                >
                  ×
                </button>
              )}
            </div>
            <div className="shop-invite-hint">
              Search by email to prepare an admin invitation.
            </div>

            {selectedUser && (
              <div className="shop-invite-selected-user">
                <RegisteredUserAvatar user={selectedUser} />
                <span className="shop-invite-result-copy">
                  <strong>{selectedUser.fullname || selectedUser.email}</strong>
                  <small>{selectedUser.email}</small>
                </span>
                <Check
                  className="shop-invite-result-check"
                  size={17}
                  aria-label="Selected"
                />
              </div>
            )}

            <AnimatePresence initial={false}>
              {hasSearchTerm && (
                <motion.div
                  id="shop-member-invite-results"
                  className="shop-invite-results"
                  role="listbox"
                  aria-label="Email invitation"
                  initial={{ opacity: 0, height: 0, y: -4 }}
                  animate={{ opacity: 1, height: "auto", y: 0 }}
                  exit={{ opacity: 0, height: 0, y: -4 }}
                  transition={{ duration: 0.18, ease: "easeOut" }}
                >
                  {isDebouncing || isFetching ? (
                    <div
                      className="shop-invite-skeleton"
                      aria-label="Searching"
                    >
                      <Skeleton variant="circular" width={34} height={34} />
                      <div>
                        <Skeleton variant="text" width={158} height={17} />
                        <Skeleton variant="text" width={112} height={14} />
                      </div>
                    </div>
                  ) : matchingUser ? (
                    <button
                      type="button"
                      className={`shop-invite-result ${
                        selectedUser?._id === matchingUser._id
                          ? "is-selected"
                          : ""
                      } ${cannotInvite ? "is-disabled" : ""}`}
                      role="option"
                      aria-selected={selectedUser?._id === matchingUser._id}
                      aria-disabled={cannotInvite}
                      disabled={cannotInvite}
                      onClick={() => {
                        if (!cannotInvite) {
                          handleSelectUser(matchingUser);
                        }
                      }}
                    >
                      <RegisteredUserAvatar user={matchingUser} />
                      <span className="shop-invite-result-copy">
                        <strong>
                          {matchingUser.fullname || matchingUser.email}
                        </strong>
                        <small>{matchingUser.email}</small>
                      </span>
                      {isOwner ? (
                        <span className="shop-invite-badge-owner">
                          Shop owner
                        </span>
                      ) : isAwaitingAcceptance ? (
                        <span className="shop-invite-badge-invited">
                          Already invited
                        </span>
                      ) : isActiveMember ? (
                        <span
                          className="shop-invite-badge-invited"
                          style={{
                            backgroundColor: "#e0f2fe",
                            color: "#0369a1",
                            borderColor: "#bae6fd",
                          }}
                        >
                          Active member
                        </span>
                      ) : selectedUser?._id === matchingUser._id ? (
                        <Check className="shop-invite-result-check" size={17} />
                      ) : (
                        <ArrowRight
                          className="shop-invite-result-arrow"
                          size={17}
                        />
                      )}
                    </button>
                  ) : noUserFound ? (
                    <div className="shop-invite-invalid" role="status">
                      No user found
                    </div>
                  ) : searchMatchesInput && isError ? (
                    <div className="shop-invite-invalid" role="alert">
                      {error?.data?.message ||
                        "Unable to search users. Try again."}
                    </div>
                  ) : !canInviteEmail ? (
                    <div className="shop-invite-invalid" role="status">
                      Enter a valid email address to continue.
                    </div>
                  ) : null}
                </motion.div>
              )}
            </AnimatePresence>

            <div className="shop-invite-actions">
              <button
                type="button"
                className="shop-invite-cancel"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="shop-invite-next"
                disabled={
                  (!selectedUser && !matchingUser) ||
                  cannotInvite ||
                  !resolvedShopId
                }
              >
                Next
                <ArrowRight size={16} />
              </button>
            </div>
          </Form>
        </Modal.Body>
      )}

      {/* STEP 2: Choose Member Access */}
      {step === 2 && (
        <Modal.Body className="shop-invite-body">
          {selectedUser && (
            <div className="shop-invite-user-banner">
              <RegisteredUserAvatar user={selectedUser} />
              <div className="shop-invite-user-banner-copy">
                <strong>{selectedUser.fullname || selectedUser.email}</strong>
                <small>{selectedUser.email}</small>
              </div>
            </div>
          )}

          <Form onSubmit={handleStep2Continue}>
            <Form.Label>Access Permissions</Form.Label>
            <div className="shop-member-access-options">
              {accessAreas.map(({ id, label }) => (
                <Form.Check
                  key={id}
                  type="checkbox"
                  id={`shop-access-${id}`}
                  className={`shop-member-access-option ${
                    selectedAreas.includes(id) ? "is-selected" : ""
                  }`}
                  label={label}
                  checked={selectedAreas.includes(id)}
                  onChange={(event) => {
                    setSelectedAreas((current) =>
                      event.target.checked
                        ? [...current, id]
                        : current.filter((area) => area !== id),
                    );
                    setAccessError("");
                  }}
                />
              ))}
            </div>

            <p className="shop-member-access-count" aria-live="polite">
              {selectedAreas.length
                ? `${selectedAreas.length} ${
                    selectedAreas.length === 1 ? "area" : "areas"
                  } selected`
                : "Choose at least one area to continue."}
            </p>

            {accessError && (
              <div className="shop-invite-invalid" role="alert">
                {accessError}
              </div>
            )}

            <div className="shop-invite-actions mt-3">
              <button
                type="button"
                className="shop-invite-back"
                onClick={() => setStep(1)}
              >
                <ArrowLeft size={16} />
                Back
              </button>
              <button
                type="button"
                className="shop-invite-cancel"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="shop-invite-next"
                disabled={isSavingAccess || selectedAreas.length === 0}
              >
                {isSavingAccess ? "Saving..." : "Next"}
                <ArrowRight size={16} />
              </button>
            </div>
          </Form>
        </Modal.Body>
      )}

      {/* STEP 3: Setup Credentials */}
      {step === 3 && (
        <Modal.Body className="shop-invite-body">
          {selectedUser && (
            <div className="shop-invite-user-banner">
              <RegisteredUserAvatar user={selectedUser} />
              <div className="shop-invite-user-banner-copy">
                <strong>{selectedUser.fullname || selectedUser.email}</strong>
                <small>{selectedUser.email}</small>
              </div>
            </div>
          )}

          <Form noValidate onSubmit={handleStep3Submit}>
            <Form.Group className="shop-access-setup-field">
              <Form.Label htmlFor="shop-access-username">Username</Form.Label>
              <Form.Control
                id="shop-access-username"
                type="text"
                value={username}
                autoComplete="username"
                placeholder="Create a username"
                required
                maxLength={20}
                isInvalid={submitted && Boolean(usernameError)}
                aria-describedby="shop-access-username-hint shop-access-username-error"
                onChange={(event) => {
                  setUsername(event.target.value);
                  setSubmitError("");
                }}
              />
              <Form.Text id="shop-access-username-hint">
                3–20 characters, letters, numbers, and allowed punctuation (._-)
              </Form.Text>
              {usernameAvailability.isFetching && (
                <div className="shop-access-loading">
                  Checking username availability...
                </div>
              )}
              {usernameAvailable === true && (
                <div className="shop-access-valid">
                  Username is available.
                </div>
              )}
              {submitted && Boolean(usernameError) && (
                <Form.Control.Feedback
                  type="invalid"
                  id="shop-access-username-error"
                >
                  {usernameError}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className="shop-access-setup-field">
              <Form.Label htmlFor="shop-access-password">Password</Form.Label>
              <div className="shop-access-password">
                <Form.Control
                  id="shop-access-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  autoComplete="new-password"
                  placeholder="Create a strong password"
                  required
                  isInvalid={submitted && Boolean(passwordError)}
                  aria-describedby="shop-access-password-hint shop-access-password-error"
                  onChange={(event) => {
                    setPassword(event.target.value);
                    setSubmitError("");
                  }}
                />
                <button
                  type="button"
                  className="shop-access-password-toggle"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <Form.Text id="shop-access-password-hint">
                At least 12 characters with uppercase, lowercase, numbers, and symbols
              </Form.Text>
              {passwordAvailability.isFetching && (
                <div className="shop-access-loading">
                  Checking password uniqueness...
                </div>
              )}
              {passwordAvailable === true && (
                <div className="shop-access-valid">
                  Password meets uniqueness requirements.
                </div>
              )}
              {submitted && Boolean(passwordError) && (
                <Form.Control.Feedback
                  type="invalid"
                  id="shop-access-password-error"
                >
                  {passwordError}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            {submitError && (
              <div className="shop-invite-invalid" role="alert">
                {submitError}
              </div>
            )}

            <div className="shop-invite-actions mt-3">
              <button
                type="button"
                className="shop-invite-back"
                onClick={() => setStep(2)}
              >
                <ArrowLeft size={16} />
                Back
              </button>
              <button
                type="button"
                className="shop-invite-cancel"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="shop-invite-next"
                disabled={isSavingCredentials || (submitted && !formIsValid)}
              >
                {isSavingCredentials ? "Saving..." : "Grant Access"}
              </button>
            </div>
          </Form>
        </Modal.Body>
      )}
    </Modal>
  );
};

export default ModalEmailone;
